import api from './api'

export interface Module {
  module_id: number
  name: string
  description?: string
  filename?: string
  material_filepath?: string
  file_type?: string
  status: number // 0 - inactive, 1 - active
  added_at: string
  updated_at: string
  access_date_start?: string
  access_date_end?: string
  time_setting?: {
    id: number
    module_id: number
    start_date: string
    end_date: string
    created_at: string
    updated_at: string
  }
}

export interface CreateModulePayload {
  name: string
  description?: string
  status?: number // 0 - inactive, 1 - active
}

export interface CalibratePayload {
  start_month?: number // 1-12, default: 1 (January)
  start_year?: number // default: current year
  cycles?: number // 1-10, default: 3
  module_order?: number[] // Custom order of module IDs
}

export interface CalibrateResponse {
  total_modules: number
  cycles_generated?: number
  cycles_previewed?: number
  start_month: string
  total_settings_created?: number
  total_settings_preview?: number
  settings?: Array<{
    cycle: number
    module_id: number
    module_name: string
    start_date: string
    end_date: string
    month_year: string
  }>
  preview?: Array<{
    cycle: number
    module_id: number
    module_name: string
    start_date: string
    end_date: string
    month_year: string
  }>
}

export interface ApiResponse<T> {
  success: boolean
  message: string
  data: T
}

export interface ModuleRotation {
  module_id: number
  module_name: string
  start_date: string
  end_date: string
  month: number
  month_name: string
  year: number
  is_current: boolean
  is_past: boolean
  is_future: boolean
}

export class ModulesService {
  static async getModules(): Promise<Module[]> {
    try {
      const response = await api.get<Module[]>('/modules')
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async getActiveModules(): Promise<Module[]> {
    try {
      const response = await api.get<Module[]>('/modules/active')
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async getInactiveModules(): Promise<Module[]> {
    try {
      const response = await api.get<Module[]>('/modules/inactive')
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async getAvailableModulesForExams(): Promise<Module[]> {
    try {
      const response = await api.get<ApiResponse<Module[]>>('/modules/available-for-exams')
      return response.data.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async getModuleRotations(year?: number): Promise<ModuleRotation[]> {
    try {
      const params = year ? { year } : {}
      const response = await api.get<ApiResponse<ModuleRotation[]>>('/modules/rotations', { params })
      return response.data.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async createModule(payload: CreateModulePayload): Promise<ApiResponse<Module>> {
    try {
      const response = await api.post<ApiResponse<Module>>('/modules', payload)
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        // For Laravel validation errors, throw the response data directly
        // This preserves the structure: { message: "...", errors: {...} }
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async updateModule(id: number, payload: Partial<CreateModulePayload>): Promise<ApiResponse<Module>> {
    try {
      const response = await api.put<ApiResponse<Module>>(`/modules/${id}`, payload)
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async deleteModule(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete<ApiResponse<any>>(`/modules/${id}`)
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async uploadModuleFile(moduleId: number, file: File): Promise<ApiResponse<{
    file_url: string
    original_filename: string
    filename: string
  }>> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post<ApiResponse<{
        file_url: string
        original_filename: string
        filename: string
      }>>(`/modules/${moduleId}/upload-file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async updateModuleStatus(id: number, status: 0 | 1): Promise<ApiResponse<{
    module_id: number
    status: number
    status_name: string
  }>> {
    try {
      const response = await api.put<ApiResponse<{
        module_id: number
        status: number
        status_name: string
      }>>(`/modules/${id}/status`, { status })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async calibrateModules(payload: CalibratePayload = {}): Promise<ApiResponse<CalibrateResponse>> {
    try {
      // Use 4-minute timeout for calibration sync (240000ms)
      const response = await api.post<ApiResponse<CalibrateResponse>>('/modules/calibrate', payload, {
        timeout: 240000
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async previewCalibration(payload: CalibratePayload = {}): Promise<ApiResponse<CalibrateResponse>> {
    try {
      // Use 4-minute timeout for calibration preview (240000ms)
      const response = await api.post<ApiResponse<CalibrateResponse>>('/modules/calibrate/preview', payload, {
        timeout: 240000
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async downloadFile(moduleId: number): Promise<Blob> {
    try {
      // Add cache-busting timestamp to prevent browser caching
      const timestamp = new Date().getTime()
      const response = await api.get(`/modules/${moduleId}/download?t=${timestamp}`, {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        // If error response is a blob, convert it to JSON
        if (error.response.data instanceof Blob) {
          const text = await error.response.data.text()
          const errorData = JSON.parse(text)
          throw errorData
        }
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async previewFile(moduleId: number): Promise<ApiResponse<{
    module_id: number
    module_name: string
    filename: string
    file_extension: string
    file_size: number
    file_size_formatted: string
    mime_type: string
    file_url: string
    is_previewable: boolean
    preview_type: string
  }>> {
    try {
      // Add cache-busting timestamp for file preview metadata
      const timestamp = new Date().getTime()
      const response = await api.get<ApiResponse<{
        module_id: number
        module_name: string
        filename: string
        file_extension: string
        file_size: number
        file_size_formatted: string
        mime_type: string
        file_url: string
        is_previewable: boolean
        preview_type: string
      }>>(`/modules/${moduleId}/preview?t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }
}

export default ModulesService
